// Hero scoring + selection. Pure functions. No React, no DOM.
//
// Pipeline (per the approved spec):
//   1. Manual override        homepageHero === "always" pinned to slot 1
//   2. Editorial gate         heroEligible only (the candidate list itself)
//   3. Cold-start curation    new visitors → diverse-sector best-of sampler
//   4. Photography quality    in-progress demoted (-0.15)
//   5. Mobile crop integrity  unsafe crops demoted (-0.10)
//   6. Global popularity      0.55 weight
//   7. Local interest         0.45 weight
//   8. Diversity pass         no back-to-back project, no >2 same sector,
//                             ≤40% sector share, ≥1 in-progress per 6
//
// Recency multiplier is applied INSIDE the per-visitor score (and inside the
// edge function for global) so newer signals always count more.

import { POPULARITY, HERO, DIVERSITY, QUALITY } from "./heroAlgorithm.config";
import type { HeroCandidate } from "./projectImagery";
import type { TasteProfile, TasteEvent } from "./visitorTaste";
import { priorityBySlug, LAST_RANK } from "@/data/projects/priority";

export { HERO, POPULARITY, DIVERSITY } from "./heroAlgorithm.config";

const recencyMult = (ageDays: number): number => {
  if (ageDays <= POPULARITY.recency.boostDays) return POPULARITY.recency.boostX;
  if (ageDays >= POPULARITY.recency.horizonDays) return 0;
  const span = POPULARITY.recency.horizonDays - POPULARITY.recency.boostDays;
  const t = (ageDays - POPULARITY.recency.boostDays) / span;
  return Math.max(POPULARITY.recency.decayFloor, 1 - t * (1 - POPULARITY.recency.decayFloor));
};

/** Editorial baseline derived from the priority list. Rank 1 → 1.0, rank 13 → ~0.08. */
const editorialBaseline = (slug: string): number => {
  const r = priorityBySlug(slug);
  if (r === LAST_RANK) return 0;
  return Math.max(0, 1 - (r - 1) / 13);
};

const editorialDateRecency = (iso: string): number => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  const days = Math.max(0, (Date.now() - t) / 86400000);
  return Math.exp(-days / 90);
};

/** Personal affinity per slug, computed from timestamped events. */
const personalAffinity = (
  candidate: HeroCandidate,
  events: TasteEvent[],
): number => {
  const now = Date.now();
  let s = 0;
  for (const e of events) {
    const ageDays = (now - e.at) / 86400000;
    const m = recencyMult(ageDays);
    if (m === 0) continue;
    if (e.slug === candidate.slug) s += e.points * m;
    else if (e.sector === candidate.sector) s += e.points * m * 0.4;
    else if (e.service === candidate.service) s += e.points * m * 0.15;
  }
  return s;
};

const normalize = (vals: number[]): number[] => {
  const max = Math.max(0, ...vals);
  if (max <= 0) return vals.map(() => 0);
  return vals.map((v) => Math.max(0, v) / max);
};

export type ScoredCandidate = HeroCandidate & { score: number; _personal: number; _global: number };

export type ScoreOptions = {
  taste: TasteProfile;
  globalPopularity?: Record<string, number>;
  /** Returning visitors on /: shuffle the pool fresh each mount, and never
   *  put the `first-visit` pinned candidate (Shy Boy) in slot 0 again. */
  freshShuffle?: boolean;
};

const fisherYates = <T,>(arr: T[]): T[] => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};


export const scoreCandidates = (
  candidates: HeroCandidate[],
  { taste, globalPopularity = {} }: ScoreOptions,
): ScoredCandidate[] => {
  // Editorial gate: drop hard-blocked entries AND in-progress photography
  // unless the project has been manually pinned via homepageHero === "always".
  // Keeps construction / placeholder imagery out of the homepage hero.
  const filtered = candidates.filter((c) => {
    if (c.homepageHero === "never") return false;
    if (c.photoQuality === "in-progress" && c.homepageHero !== "always") return false;
    return true;
  });

  const personalRaw = filtered.map((c) => personalAffinity(c, taste.events));
  const personalNorm = normalize(personalRaw);
  const globalNorm = filtered.map((c) => globalPopularity[c.slug] ?? 0);
  const recent = new Set(taste.shownRecently);

  return filtered.map((c, i) => {
    const editorial = editorialBaseline(c.slug) * 0.7
                    + editorialDateRecency(c.updated) * 0.3;

    const popularity =
      POPULARITY.weights.global * globalNorm[i] +
      POPULARITY.weights.personal * personalNorm[i];

    // Engagement-driven layer is a SOFT modifier, not a re-ranker.
    // Editorial order is preserved unless a project gets meaningful traction.
    let score =
      0.70 * editorial +
      0.30 * popularity;

    if (c.photoQuality === "in-progress") score += QUALITY.inProgressPenalty;
    if (!c.mobileCropSafe) score += QUALITY.mobileUnsafePenalty;
    if (recent.has(c.slug)) score -= DIVERSITY.recentShownPenalty;

    return { ...c, score, _personal: personalNorm[i], _global: globalNorm[i] };
  });
};


/** Pinned candidates ("always" / "first-visit") beat score; otherwise highest score wins. */
const preferAlways = (a: ScoredCandidate, b: ScoredCandidate): ScoredCandidate => {
  const aPin = a.homepageHero === "always" || a.homepageHero === "first-visit";
  const bPin = b.homepageHero === "always" || b.homepageHero === "first-visit";
  if (aPin && !bPin) return a;
  if (bPin && !aPin) return b;
  return a.score >= b.score ? a : b;
};

/** Cold start: walk the editorial priority list top-to-bottom, picking the
 *  best image per project. Editorial order is preserved exactly. */
const editorialOrderSampler = (scored: ScoredCandidate[]): ScoredCandidate[] => {
  const bySlugBest = new Map<string, ScoredCandidate>();
  for (const c of scored) {
    const prev = bySlugBest.get(c.slug);
    bySlugBest.set(c.slug, prev ? preferAlways(prev, c) : c);
  }
  // Sort by editorial rank (priorityBySlug): rank 1 first, rank 13 last.
  return [...bySlugBest.values()].sort(
    (a, b) => priorityBySlug(a.slug) - priorityBySlug(b.slug),
  );
};

/** Dedupe to one image per slug; "always"-pinned beats score. */
const dedupeBySlug = (arr: ScoredCandidate[]): ScoredCandidate[] => {
  const best = new Map<string, ScoredCandidate>();
  for (const c of arr) {
    const prev = best.get(c.slug);
    best.set(c.slug, prev ? preferAlways(prev, c) : c);
  }
  // Preserve original order
  const seen = new Set<string>();
  const out: ScoredCandidate[] = [];
  for (const c of arr) {
    if (seen.has(c.slug)) continue;
    seen.add(c.slug);
    out.push(best.get(c.slug)!);
  }
  return out;
};

/** Greedy reorder: enforce no-repeat within a sliding window + sector-run cap. */
const enforceDiversity = (arr: ScoredCandidate[]): ScoredCandidate[] => {
  const out: ScoredCandidate[] = [];
  const remaining = [...arr];
  const windowSize = DIVERSITY.projectRepeatWindow;
  let sectorRun = 0;

  while (remaining.length) {
    const lastSector = out[out.length - 1]?.sector;
    const windowSlugs = new Set(
      out.slice(Math.max(0, out.length - windowSize)).map((c) => c.slug),
    );

    // Try strict: no slug in window + sector-run cap
    let pickIdx = remaining.findIndex(
      (c) => !windowSlugs.has(c.slug) && !(sectorRun >= 2 && c.sector === lastSector),
    );
    // Relax sector rule
    if (pickIdx === -1) {
      pickIdx = remaining.findIndex((c) => !windowSlugs.has(c.slug));
    }
    // Relax window to just no-back-to-back
    if (pickIdx === -1 && DIVERSITY.noBackToBackProject) {
      const lastSlug = out[out.length - 1]?.slug;
      pickIdx = remaining.findIndex((c) => c.slug !== lastSlug);
    }
    if (pickIdx === -1) pickIdx = 0; // unavoidable

    const picked = remaining.splice(pickIdx, 1)[0];
    sectorRun = picked.sector === lastSector ? sectorRun + 1 : 1;
    out.push(picked);
  }
  return out;
};

/** Round-robin interleave: take one image per slug, then the next, etc.
 *  Ensures every project plays its 1st image before any project's 2nd. */
const interleaveBySlug = (arr: ScoredCandidate[]): ScoredCandidate[] => {
  const buckets = new Map<string, ScoredCandidate[]>();
  for (const c of arr) {
    const list = buckets.get(c.slug) ?? [];
    list.push(c);
    buckets.set(c.slug, list);
  }
  // Preserve slug order by first appearance, but ordered by editorial rank.
  const slugs = [...buckets.keys()].sort(
    (a, b) => priorityBySlug(a) - priorityBySlug(b),
  );
  const out: ScoredCandidate[] = [];
  let added = true;
  let i = 0;
  while (added) {
    added = false;
    for (const slug of slugs) {
      const list = buckets.get(slug) ?? [];
      if (i < list.length) {
        out.push(list[i]);
        added = true;
      }
    }
    i++;
  }
  return out;
};

export const selectHeroSlides = (
  candidates: HeroCandidate[],
  opts: ScoreOptions,
): ScoredCandidate[] => {
  const scored = scoreCandidates(candidates, opts);
  const hasGlobal = opts.globalPopularity && Object.keys(opts.globalPopularity).length > 0;
  const coldStart =
    opts.taste.signalCount < HERO.coldStartSignalThreshold && !hasGlobal;

  let pool: ScoredCandidate[];
  if (opts.freshShuffle) {
    // Returning visitor: shuffle the SLUG ORDER (not individual images), then
    // interleave so every project's 1st image plays before any 2nd image,
    // 2nd before any 3rd, etc. No repeats until the full pool is exhausted.
    const uniqueSlugs = Array.from(new Set(scored.map((c) => c.slug)));
    const shuffledSlugs = fisherYates(uniqueSlugs);
    const slugRank = new Map(shuffledSlugs.map((s, i) => [s, i]));
    const sorted = [...scored].sort(
      (a, b) => (slugRank.get(a.slug)! - slugRank.get(b.slug)!),
    );
    // interleaveBySlug sorts by editorial rank; override for fresh shuffle.
    const buckets = new Map<string, ScoredCandidate[]>();
    for (const c of sorted) {
      const list = buckets.get(c.slug) ?? [];
      list.push(c);
      buckets.set(c.slug, list);
    }
    const out: ScoredCandidate[] = [];
    let added = true;
    let i = 0;
    while (added) {
      added = false;
      for (const slug of shuffledSlugs) {
        const list = buckets.get(slug) ?? [];
        if (i < list.length) {
          out.push(list[i]);
          added = true;
        }
      }
      i++;
    }
    pool = out.slice(0, HERO.poolSize);
  } else if (coldStart) {
    // True first-ever visit: strict editorial order, one image per project
    // for the first cycle, then continue with the remaining images of each
    // project interleaved (round-robin) so nothing repeats until exhausted.
    pool = interleaveBySlug(scored);
  } else {
    // Fallback (engaged visitor without freshShuffle flag): editorial rank
    // first, then score; keep ALL images so the rotation plays through the
    // full featured pool before looping.
    const sorted = [...scored].sort((a, b) => {
      const ra = priorityBySlug(a.slug);
      const rb = priorityBySlug(b.slug);
      if (ra !== rb) return ra - rb;
      return b.score - a.score;
    });
    pool = interleaveBySlug(sorted).slice(0, HERO.poolSize);
  }

  // Pin block.
  // - freshShuffle (returning visitor): swap any first-visit-pinned candidate
  //   out of slot 0, then move the "always" pin (if any) to slot 0.
  // - else: keep the legacy cold-start / always pin logic.
  if (opts.freshShuffle) {
    if (pool[0]?.homepageHero === "first-visit") {
      const swapIdx = pool.findIndex(
        (c, i) => i > 0 && c.homepageHero !== "first-visit",
      );
      if (swapIdx > 0) {
        [pool[0], pool[swapIdx]] = [pool[swapIdx], pool[0]];
      }
    }
    const alwaysPin = pool.find((c) => c.homepageHero === "always");
    if (alwaysPin) {
      pool = [alwaysPin, ...pool.filter((c) => c !== alwaysPin)];
    }
  } else {
    const firstVisitPin = coldStart
      ? pool.find((c) => c.homepageHero === "first-visit")
      : undefined;
    const pinned = firstVisitPin ?? pool.find((c) => c.homepageHero === "always");
    if (pinned) {
      pool = [pinned, ...pool.filter((c) => c !== pinned)];
    }
  }


  pool = enforceDiversity(pool);
  return pool;
};


